import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { inject } from '@angular/core';
import { PermissionService } from './permissions.service';
import { getCurrentUser } from 'aws-amplify/auth';

export const authGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const permissionService = inject(PermissionService);

  // 1. Check for a valid token
  try {
    await getCurrentUser();
  } catch {
    router.navigate(['/login']);
    return false;
  }

  // 2. Now handle specific routes by checking Permissions
  //    (instead of roles). If the user does NOT have permission,
  //    redirect them to the first route they *do* have access to.

  // -- /events -> requires "view:hero"
  if (state.url.startsWith('/events') && !permissionService.can('view:hero')) {
    const fallback = getFirstPermittedRoute(permissionService);
    router.navigateByUrl(fallback);
    return false;
  }

  // -- /user-management -> requires "view:users"
  if (state.url.startsWith('/user-management') && !permissionService.can('view:users')) {
    const fallback = getFirstPermittedRoute(permissionService);
    router.navigateByUrl(fallback);
    return false;
  }

  // -- /organizations -> requires "view:organizations"
  if (state.url.startsWith('/organizations') && !permissionService.can('view:organizations')) {
    const fallback = getFirstPermittedRoute(permissionService);
    router.navigateByUrl(fallback);
    return false;
  }

  // 3. If none of the above checks blocked access, let them in!
  return true;
};

/**
 * Return the "first permitted route" the user actually can see,
 * in the priority order you want.
 */
function getFirstPermittedRoute(permissionService: PermissionService): string {
  // If user can view hero => /events
  if (permissionService.can('view:hero')) {
    return '/events';
  }
  // Else if user can view users => /user-management
  if (permissionService.can('view:users')) {
    return '/user-management';
  }
  // Else if user can view organizations => /organizations
  if (permissionService.can('view:organizations')) {
    return '/organizations';
  }

  // If they don't have any of those, you could send them
  // to /account-settings or /login or whichever fallback you prefer
  return '/account-settings';
}
